import { useExportDuoContractsMutation } from "@/api/mutations/duo/useExportDuoContractsMutation"
import { useDuoContractExportsQuery } from "@/api/queries/duo/useDuoContractExportsQuery"
import type {
  DuoContractExportValidationError,
  DuoExport,
} from "@/api/types/duo"
import { ContractExportDetail } from "@/components/duo/ContractExportDetail"
import { DuoSidebarActionButton } from "@/components/duo/DuoSidebarActionButton"
import { DuoWorkspaceLayout } from "@/components/duo/DuoWorkspaceLayout"
import { checkPermissions } from "@/utils/checkPermissions"
import { ChevronRightIcon } from "@heroicons/react/24/outline"
import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router"
import {
  ApiValidationError,
  PageHeader,
  Skeleton,
  formatDateToDutch,
} from "toptaal-shared"
import { z } from "zod"

const searchSchema = z.object({
  exportId: z.number().optional(),
})

export const Route = createFileRoute("/duo/contractenlijsten/")({
  component: RouteComponent,
  validateSearch: searchSchema,
  beforeLoad: () => checkPermissions("read", "duo"),
})

function RouteComponent() {
  const { exportId } = useSearch({ from: "/duo/contractenlijsten/" })
  const navigate = useNavigate({ from: "/duo/contractenlijsten/" })
  const {
    data: exports = [],
    isLoading,
    isError,
  } = useDuoContractExportsQuery()
  const exportMutation = useExportDuoContractsMutation()
  const selectedExport = getSelectedExport(exports, exportId)
  const validationErrors = getContractValidationErrors(exportMutation.error)

  const selectExport = (selectedId: number) => {
    navigate({ search: { exportId: selectedId }, replace: true })
  }

  const createExport = () => {
    exportMutation.mutate(undefined, {
      onSuccess: () => navigate({ search: {}, replace: true }),
    })
  }

  return (
    <DuoWorkspaceLayout
      sidebar={
        <>
          <PageHeader>DUO contractenlijsten</PageHeader>

          <DuoSidebarActionButton
            isLoading={exportMutation.isPending}
            loadingLabel="Export maken..."
            onClick={createExport}
          >
            Nieuwe export
          </DuoSidebarActionButton>

          {validationErrors.length > 0 && (
            <ContractValidationErrors errors={validationErrors} />
          )}

          {exportMutation.isError && validationErrors.length === 0 && (
            <p className="mt-4 text-sm text-red" role="alert">
              De DUO-contractenlijst kon niet worden aangemaakt.
            </p>
          )}

          <div className="mt-3 border-t border-primary-accent-blue pt-3">
            {isLoading && <ExportHistorySkeleton />}

            {isError && (
              <p className="text-sm text-red" role="alert">
                De exportgeschiedenis kon niet worden geladen.
              </p>
            )}

            {!isLoading && !isError && exports.length === 0 && (
              <p className="py-3 text-sm text-primary-dark-blue/70">
                Er zijn nog geen contractenlijsten geëxporteerd.
              </p>
            )}

            <div className="flex flex-col gap-2">
              {exports.map((duoExport) => {
                const isSelected = selectedExport?.id === duoExport.id

                return (
                  <button
                    key={duoExport.id}
                    type="button"
                    aria-current={isSelected ? "page" : undefined}
                    className={`flex h-11 w-full items-center justify-between rounded px-4 text-left transition-colors ${
                      isSelected
                        ? "bg-primary-light-blue"
                        : "bg-light-grey hover:bg-primary-light-blue"
                    }`}
                    onClick={() => selectExport(duoExport.id)}
                  >
                    <span>{formatDateToDutch(duoExport.insertedAt)}</span>
                    <ChevronRightIcon className="size-5" />
                  </button>
                )
              })}
            </div>
          </div>
        </>
      }
    >
      {selectedExport ? (
        <ContractExportDetail duoExport={selectedExport} />
      ) : (
        !isLoading && (
          <div className="flex h-full min-h-56 items-center justify-center text-center text-primary-dark-blue/70">
            Maak een nieuwe export om een DUO-contractenlijst te downloaden.
          </div>
        )
      )}
    </DuoWorkspaceLayout>
  )
}

function getSelectedExport(exports: DuoExport[], exportId?: number) {
  return exports.find((duoExport) => duoExport.id === exportId) ?? exports[0]
}

function getContractValidationErrors(error: unknown) {
  if (!(error instanceof ApiValidationError)) return []

  const response = error.response.data as unknown as {
    errors?: DuoContractExportValidationError[]
  }

  return response?.errors ?? []
}

function ContractValidationErrors({
  errors,
}: {
  errors: DuoContractExportValidationError[]
}) {
  return (
    <div
      className="mt-4 rounded border border-red/30 bg-red/5 p-3"
      role="alert"
    >
      <p className="text-sm font-bold text-red">
        De export bevat ongeldige contractgegevens:
      </p>
      <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-primary-dark-blue">
        {errors.map((error, index) => (
          <li key={`${error.studentProjectOrganizationId ?? "error"}-${index}`}>
            {error.contractNumber && `${error.contractNumber}: `}
            {error.errors?.join(", ") ?? error.message ?? "Onbekende fout"}
          </li>
        ))}
      </ul>
    </div>
  )
}

function ExportHistorySkeleton() {
  return (
    <div className="flex flex-col gap-2" aria-label="Exportgeschiedenis laden">
      {Array.from({ length: 4 }, (_, index) => (
        <Skeleton key={index} className="h-11 w-full" />
      ))}
    </div>
  )
}
